import { useState, useMemo, useRef } from 'react';
import classnames from 'classnames';
import { useToggle, useClickAway, useTimeoutFn } from 'react-use';
import { isMobile } from 'react-device-detect';

import SelectMenu from 'components/atoms/SelectMenu';

import type { Option } from 'components/atoms/SelectMenu';

import styles from './index.css';

interface Icon {
    default: string;
    hover: string;
}
export interface IconProp {
    className?: string;
    icon: Icon;
    label?: string;

    focus?: boolean;

    active?: boolean;
    activeIcon?: Icon;
    activeLabel?: string;
    onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

    options?: Option[];
    optionsReverse?: boolean;
    onClickOption?: (value: Option) => void;
}

const Icon: React.FC<IconProp> = ({
    className,
    icon: defaultIcon,
    label: defaultLabel,

    active,
    activeIcon,
    activeLabel,
    onClick = () => {},

    options = [],
    optionsReverse = false,
    onClickOption = () => {},
}) => {
    const ref = useRef<HTMLButtonElement>(null);

    const [hovering, setHovering] = useState(false);
    const [menuActive, toggleMenu] = useToggle(false);

    const label = useMemo(() => {
        if (activeLabel && active) {
            return activeLabel;
        }

        return defaultLabel;
    }, [active]);

    const icon = useMemo(() => {
        if (activeIcon && active) {
            return activeIcon;
        }

        return defaultIcon;
    }, [activeIcon, active]);

    useClickAway(ref, () => {
        toggleMenu(false);
    });

    const [, , reset] = useTimeoutFn(() => {
        setHovering(false);
    }, 400);

    return (
        <button
            ref={ref}
            className={classnames(styles.icon, className)}
            type="button"
            onMouseEnter={() => {
                if (!isMobile) {
                    setHovering(true);
                }
            }}
            onMouseLeave={() => {
                if (!isMobile) {
                    setHovering(false);
                }
            }}
            onTouchStart={() => {
                setHovering(true);
                reset();
            }}
            onClick={(event) => {
                onClick(event);

                if (options.length > 0) {
                    toggleMenu();
                }
            }}
            onBlur={(event) => {
                if (!event.relatedTarget) {
                    toggleMenu(false);
                }
            }}
        >
            {label && (hovering || menuActive) && <span className={styles.label}>{label}</span>}
            <img src={hovering || menuActive ? icon?.hover : icon?.default} alt="icon" />
            {menuActive && options.length > 0 && (
                <SelectMenu
                    options={options}
                    reverse={optionsReverse}
                    onClickOption={(value) => {
                        onClickOption(value);
                        toggleMenu(false);
                        setHovering(false);
                    }}
                />
            )}
        </button>
    );
};

export default Icon;
